import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Popconfirm,
  Statistic,
  Row,
  Col,
  DatePicker,
  InputNumber,
  Input,
  Form,
  Modal,
  Select,
  Descriptions,
  Divider,
  Typography,
  Row as AntRow,
  Col as AntCol,
} from 'antd';
import {
  SafetyOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UserOutlined,
  CalendarOutlined,
  DesktopOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { adminService, License, PaginatedResponse, LicenseItem } from '../services/admin';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface LicenseListProps {
  onCreateLicense?: () => void;
  onEditLicense?: (licenseId: number) => void;
}

const LicenseList: React.FC<LicenseListProps> = ({ onCreateLicense, onEditLicense }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteLicenseId, setDeleteLicenseId] = useState<number | null>(null);
  const [modalItemsPage, setModalItemsPage] = useState(1);
  const [modalItemsPageSize, setModalItemsPageSize] = useState(5);
  const [fileNameByFileId, setFileNameByFileId] = useState<Record<string, string | null>>({});

  // Fetch licenses with pagination
  const { data: licensesData, isLoading, refetch } = useQuery<PaginatedResponse<License>>({
    queryKey: ['licenses', page, pageSize],
    queryFn: () => adminService.getLicenses(page, pageSize),
  });

  const { data: modalItemsData, isLoading: loadingModalItems } = useQuery<PaginatedResponse<LicenseItem>>({
    queryKey: ['license-items-paged', selectedLicense?.id, modalItemsPage, modalItemsPageSize],
    queryFn: () => adminService.getLicenseItemsPaged(selectedLicense!.id, modalItemsPage, modalItemsPageSize),
    enabled: detailsModalVisible && !!selectedLicense?.id,
  });

  const handleViewLicense = (license: License) => {
    setSelectedLicense(license);
    setModalItemsPage(1);
    setDetailsModalVisible(true);
  };
  const modalItems = modalItemsData?.data || [];
  const modalItemsTotal = modalItemsData?.total || 0;

  useEffect(() => {
    const fileIds = modalItems.map((i) => i.fileId).filter(Boolean);
    const missing = fileIds.filter((fileId) => !(fileId in fileNameByFileId));
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        missing.map(async (fileId) => {
          try {
            const content = await adminService.getContentByFileId(fileId);
            return { fileId, filename: content?.filename ?? null };
          } catch {
            return { fileId, filename: null };
          }
        })
      );
      if (cancelled) return;
      setFileNameByFileId((prev) => {
        const next = { ...prev };
        for (const r of results) next[r.fileId] = r.filename;
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [modalItems, fileNameByFileId]);

  const handleEditLicense = (licenseId: number) => {
    if (onEditLicense) {
      onEditLicense(licenseId);
    } else {
      message.info('License editing requires navigation callback. Please check with system administrator.');
    }
  };

  const handleNewLicense = () => {
    if (onCreateLicense) {
      onCreateLicense();
    } else {
      message.info('License creation requires navigation callback. Please check with system administrator.');
    }
  };

  const confirmDeleteLicense = (licenseId: number) => {
    setDeleteLicenseId(licenseId);
    setDeleteModalVisible(true);
  };

  const handleDeleteLicense = async () => {
    if (!deleteLicenseId) return;

    try {
      await adminService.deleteLicense(deleteLicenseId);
      message.success('License deleted successfully');
      refetch();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete license');
    } finally {
      setDeleteModalVisible(false);
      setDeleteLicenseId(null);
    }
  };

  const getExpirationStatus = (validUntil: string) => {
    const now = dayjs();
    const expiration = dayjs(validUntil);

    if (expiration.isBefore(now)) {
      return { color: 'red', text: 'Expired' };
    } else if (expiration.diff(now, 'days') <= 7) {
      return { color: 'orange', text: 'Expiring Soon' };
    } else {
      return { color: 'green', text: 'Active' };
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'User',
      key: 'user',
      render: (_: string, record: License) => (
        <Space>
          <UserOutlined />
          <div>
            <div>{record.user.username}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.user.email}</div>
          </div>
        </Space>
      ),
      sorter: (a: License, b: License) => a.user.username.localeCompare(b.user.username),
    },
    {
      title: 'Issued',
      dataIndex: 'issuedAt',
      key: 'issuedAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
      sorter: (a: License, b: License) => new Date(a.issuedAt).getTime() - new Date(b.issuedAt).getTime(),
    },
    {
      title: 'Valid Until',
      dataIndex: 'validUntil',
      key: 'validUntil',
      render: (date: string) => {
        const status = getExpirationStatus(date);
        return (
          <Space>
            <Tag color={status.color}>{status.text}</Tag>
            <span>{dayjs(date).format('YYYY-MM-DD')}</span>
          </Space>
        );
      },
      sorter: (a: License, b: License) => new Date(a.validUntil).getTime() - new Date(b.validUntil).getTime(),
    },
    {
      title: 'Max Devices',
      dataIndex: 'maxDevices',
      key: 'maxDevices',
      render: (count: number) => (
        <Space>
          <DesktopOutlined />
          {count}
        </Space>
      ),
      sorter: (a: License, b: License) => a.maxDevices - b.maxDevices,
    },
    {
      title: 'Concurrent Plays',
      dataIndex: 'maxConcurrentPlays',
      key: 'maxConcurrentPlays',
      render: (count: number) => (
        <Space>
          <PlayCircleOutlined />
          {count}
        </Space>
      ),
      sorter: (a: License, b: License) => a.maxConcurrentPlays - b.maxConcurrentPlays,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => description || <span style={{ color: '#ccc' }}>No description</span>,
    },
    {
      title: 'Items',
      key: 'items',
      render: (_: string, record: License) => {
        const itemsCount = record.itemsCount ?? record.items?.length ?? 0;
        return (
          <Tag color="blue">
            {itemsCount} {itemsCount === 1 ? 'file' : 'files'}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: string, record: License) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewLicense(record)}
          >
            View
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditLicense(record.id)}
          >
            Edit
          </Button>
          <Popconfirm
            title="确定要删除这个许可证吗？"
            description="此操作将删除许可证及其所有文件项。"
            onConfirm={() => confirmDeleteLicense(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Licenses"
              value={licensesData?.total || 0}
              prefix={<SafetyOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Licenses"
              value={licensesData?.data?.filter((license: License) =>
                dayjs(license.validUntil).isAfter(dayjs())
              ).length || 0}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Expired Licenses"
              value={licensesData?.data?.filter((license: License) =>
                dayjs(license.validUntil).isBefore(dayjs())
              ).length || 0}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ marginBottom: 8 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleNewLicense}
                style={{ width: '100%' }}
              >
                New License
              </Button>
            </div>
            <div>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => refetch()}
                style={{ width: '100%' }}
              >
                Refresh
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      <Card title="License Management">
        <Table
          dataSource={licensesData?.data || []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: licensesData?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => `Total ${total} licenses`,
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize);
            },
          }}
        />
      </Card>

      {/* License Details Modal */}
      <Modal
        title="License Details"
        open={detailsModalVisible}
        onCancel={() => {
          setDetailsModalVisible(false);
          setSelectedLicense(null);
          setModalItemsPage(1);
        }}
        footer={null}
        width={900}
      >
        {selectedLicense && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="License ID">{selectedLicense.id}</Descriptions.Item>
              <Descriptions.Item label="User">{selectedLicense.user.username}</Descriptions.Item>
              <Descriptions.Item label="Email">{selectedLicense.user.email}</Descriptions.Item>
              <Descriptions.Item label="Issued">{dayjs(selectedLicense.issuedAt).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
              <Descriptions.Item label="Valid Until">
                <Tag color={getExpirationStatus(selectedLicense.validUntil).color}>
                  {dayjs(selectedLicense.validUntil).format('YYYY-MM-DD HH:mm')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Max Devices">{selectedLicense.maxDevices}</Descriptions.Item>
              <Descriptions.Item label="Max Concurrent Plays">{selectedLicense.maxConcurrentPlays}</Descriptions.Item>
              <Descriptions.Item label="Max Play Count">{selectedLicense.maxPlayCount.toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {selectedLicense.description || 'No description'}
              </Descriptions.Item>
            </Descriptions>

            <Divider />
            <Title level={4}>Licensed Files ({modalItemsTotal})</Title>

            <Table
              dataSource={modalItems}
              columns={[
                {
                  title: 'Content File',
                  dataIndex: 'fileId',
                  key: 'fileId',
                  width: 200,
                  render: (fileId: string) => {
                    const filename = fileNameByFileId[fileId];
                    return (
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {filename || fileId}
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>
                          ID: {fileId}
                        </div>
                      </div>
                    );
                  },
                },
                {
                  title: 'Content Type',
                  dataIndex: 'contentType',
                  key: 'contentType',
                  render: (type: string) => <Tag color="blue">{type}</Tag>,
                  width: 120,
                },
                {
                  title: 'Valid Until',
                  dataIndex: 'validUntil',
                  key: 'validUntil',
                  render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
                },
                {
                  title: 'Max Play Count',
                  dataIndex: 'maxPlayCount',
                  key: 'maxPlayCount',
                  render: (count: number) => count.toLocaleString(),
                },
                {
                  title: 'Encrypted Key',
                  dataIndex: 'encryptedKey',
                  key: 'encryptedKey',
                  render: (key: string) => (
                    <span style={{ fontFamily: 'monospace', fontSize: '10px' }}>
                      {key.substring(0, 20)}...
                    </span>
                  ),
                  width: 200,
                },
              ]}
              rowKey="id"
              loading={loadingModalItems}
              pagination={{
                current: modalItemsPage,
                pageSize: modalItemsPageSize,
                total: modalItemsTotal,
                showTotal: (total) => `${total} files`,
                onChange: (p, ps) => {
                  setModalItemsPage(p);
                  setModalItemsPageSize(ps);
                },
              }}
              locale={{
                emptyText: loadingModalItems ? 'Loading license files...' : 'No licensed files found',
              }}
            />
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete License"
        open={deleteModalVisible}
        onOk={handleDeleteLicense}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
      >
        <p>Are you sure you want to delete this license? This action cannot be undone.</p>
        {deleteLicenseId && (
          <p>
            This will delete the license and all{' '}
            {licensesData?.data?.find(l => l.id === deleteLicenseId)?.itemsCount || 0}{' '}
            associated license items.
          </p>
        )}
      </Modal>
    </div>
  );
};

export default LicenseList;
